import { API_BASE, DEMO_MODE, type ActiveToolInfo } from '@/types/machine.types';
import endMillArt from '@/assets/tools/end-mill.webp';
import endMillLargeArt from '@/assets/tools/end-mill.lg.webp';
import ballNoseArt from '@/assets/tools/ball-nose.webp';
import ballNoseLargeArt from '@/assets/tools/ball-nose.lg.webp';
import rEndMillArt from '@/assets/tools/r-end-mill.webp';
import rEndMillLargeArt from '@/assets/tools/r-end-mill.lg.webp';
import engravingArt from '@/assets/tools/engraving.webp';
import engravingLargeArt from '@/assets/tools/engraving.lg.webp';
import faceMillArt from '@/assets/tools/face-mill.webp';
import faceMillLargeArt from '@/assets/tools/face-mill.lg.webp';
import probeArt from '@/assets/tools/probe.webp';
import probeLargeArt from '@/assets/tools/probe.lg.webp';
import drillArt from '@/assets/tools/drill.webp';
import drillLargeArt from '@/assets/tools/drill.lg.webp';

// The tool catalogue is a closed set, so art is keyed by tool *type* — never by
// slot number, since a pocket can hold a different tool tomorrow.
export type ToolType =
  | 'End Mill'
  | 'Ball Nose'
  | 'R-End Mill'
  | 'Engraving'
  | 'Face Mill'
  | 'Probe'
  | 'Drill';

interface ToolArt {
  /** Small rotated image used in the table. */
  thumb: string;
  /** Hi-res variant for the "Selected Tool" preview; falls back to `thumb`. */
  large?: string;
}

// Record over the union: adding a tool type without art is a compile error, so
// no runtime fallback is needed — a fallback would silently draw the wrong tool.
export const TOOL_ART: Record<ToolType, ToolArt> = {
  'End Mill': { thumb: endMillArt, large: endMillLargeArt },
  'Ball Nose': { thumb: ballNoseArt, large: ballNoseLargeArt },
  'R-End Mill': { thumb: rEndMillArt, large: rEndMillLargeArt },
  Engraving: { thumb: engravingArt, large: engravingLargeArt },
  'Face Mill': { thumb: faceMillArt, large: faceMillLargeArt },
  Probe: { thumb: probeArt, large: probeLargeArt },
  Drill: { thumb: drillArt, large: drillLargeArt },
};

// Order mirrors the catalogue; drives the "Tool Type" <select> in the form modal.
export const TOOL_TYPES: readonly ToolType[] = [
  'End Mill',
  'Ball Nose',
  'R-End Mill',
  'Engraving',
  'Face Mill',
  'Probe',
  'Drill',
];

// Fixed flute-count choices for the tool form's dropdown. The catalogue only
// ever uses these, so the list is a closed set rather than free text.
export const FLUTE_OPTIONS: readonly string[] = ['1F', '2F', '3F', '4F'];

// Upscaling a tool's own thumb is preferable to showing another tool's art
// while the remaining `.lg` renders are being produced.
export function largeArt(type: ToolType): string {
  const art = TOOL_ART[type];
  return art.large ?? art.thumb;
}

/** Persisted offset state. Not derived from form fields: it flips to 'measured'
 *  only after the Get Offset operation, and back to 'unMeasured' on save. */
export type OffsetStatus = 'measured' | 'unMeasured';

export interface ToolRow {
  no: string;
  /** Empty string marks an empty pocket (no tool assigned); Define Tool sets it. */
  type: ToolType | '';
  diameter: string;
  /** Overall tool length (mm). Optional so older stored rows stay readable. */
  toolLength?: string;
  helix: string;
  flute: string;
  offsetStatus: OffsetStatus;
  /** Session-only "last measured" time for the modal; not persisted to JSON
   *  (the schema is fixed to the six machine/handoff fields). */
  measuredAt?: string;
}

/** The ten physical pockets are fixed; the JSON only carries the filled ones. */
export const TOOL_NUMBERS: readonly string[] = [
  'T01',
  'T02',
  'T03',
  'T04',
  'T05',
  'T06',
  'T07',
  'T08',
  'T09',
  'T10',
];

/** Strips a trailing unit (e.g. "6.00 mm" → "6.00") for a clean numeric display. */
export function numericPart(value: string): string {
  return value.replace(/\s*mm$/i, '');
}

/** Tool number from a slot label ("T05" → 5); 0 for a malformed/empty label. */
export function toolNumberOf(no: string): number {
  const n = parseInt(no.replace(/\D/g, ''), 10);
  return Number.isNaN(n) ? 0 : n;
}

/** Slot label from a tool number (5 → "T05"), the inverse of toolNumberOf. */
export function formatToolNo(n: number): string {
  return `T${String(n).padStart(2, '0')}`;
}

export function formatMm(value: string): string {
  const n = parseFloat(value.replace(',', '.'));
  return Number.isNaN(n) ? '' : `${n.toFixed(2)} mm`;
}

/** Formats an ISO timestamp as "DD.MM.YYYY HH:mm" for the offset "measured at" line. */
export function formatMeasuredAt(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Blanks a slot on removal: only the T-number survives, matching an empty pocket. */
export function emptySlot(no: string): ToolRow {
  return { no, type: '', diameter: '', helix: '', flute: '', offsetStatus: 'unMeasured' };
}

/** An empty pocket has no tool assigned — derived from a missing type. */
export function isEmpty(tool: ToolRow): boolean {
  return tool.type === '';
}

/** A tool is "measured" only when the Get Offset operation set the flag. */
export function isMeasured(tool: ToolRow): boolean {
  return tool.offsetStatus === 'measured';
}

// ==========================================================================
// Backend persistence (JSON on the Pi, shared with the LinuxCNC remap python)
// ==========================================================================

/** One tool's on-disk shape. Field names are the machine/handoff contract and
 *  differ from ToolRow's UI names (type/helix/flute), so we map at this boundary. */
interface ToolJson {
  toolType: ToolType | '';
  diameter: string;
  toolLength: string;
  helixLength: string;
  flutes: string;
  offsetStatus: OffsetStatus;
}

interface ToolTableJson {
  ToolTable: Record<string, Partial<ToolJson>>;
}

/** The Pi tool table is untrusted: the remap python has written numeric 0 where
 *  a string is expected, which `??` does not catch (0 ?? '' is 0) and corrupts
 *  the string-typed row. Coerce to string at this boundary, but warn so a
 *  machine-side regression stays visible instead of silently healing. */
function coerceStr(value: unknown, field: string, no: string): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  console.warn(
    `tool table ${no}: "${field}" expected string, got ${typeof value} (${String(value)})`,
  );
  return String(value);
}

/** Like coerceStr but also rejects a string that is not a known tool type. */
function coerceToolType(value: unknown, no: string): ToolType | '' {
  const s = coerceStr(value, 'toolType', no);
  if (s === '' || (TOOL_TYPES as readonly string[]).includes(s)) return s as ToolType | '';
  console.warn(`tool table ${no}: "${s}" is not a known tool type`);
  return '';
}

function rowFromJson(no: string, entry: Partial<ToolJson>): ToolRow {
  return {
    no,
    type: coerceToolType(entry.toolType, no),
    diameter: coerceStr(entry.diameter, 'diameter', no),
    toolLength: coerceStr(entry.toolLength, 'toolLength', no),
    helix: coerceStr(entry.helixLength, 'helixLength', no),
    flute: coerceStr(entry.flutes, 'flutes', no),
    offsetStatus: entry.offsetStatus === 'measured' ? 'measured' : 'unMeasured',
  };
}

function jsonFromRow(tool: ToolRow): ToolJson {
  return {
    toolType: tool.type,
    diameter: tool.diameter,
    toolLength: tool.toolLength ?? '',
    helixLength: tool.helix,
    flutes: tool.flute,
    offsetStatus: tool.offsetStatus,
  };
}

/** Builds the store's tool geometry cache (number → type/diameter) from the
 *  table rows, skipping empty pockets. The store resolves the active tool's
 *  spec from this by matching the machine-reported spindle number. */
export function buildToolCatalog(tools: ToolRow[]): Record<number, ActiveToolInfo> {
  const catalog: Record<number, ActiveToolInfo> = {};
  for (const tool of tools) {
    if (isEmpty(tool)) continue;
    catalog[toolNumberOf(tool.no)] = {
      no: tool.no,
      type: tool.type,
      diameter: tool.diameter,
    };
  }
  return catalog;
}

/** Seed tools shown in the web/demo build, where there is no Pi backend to read
 *  from. Baked into the static bundle so every visitor sees the same table (one
 *  of each catalogue type in T01..T07; T08..T10 stay empty). */
const DEMO_TOOLS: ToolRow[] = [
  {
    no: 'T01',
    type: 'End Mill',
    diameter: '6.00 mm',
    toolLength: '58.00 mm',
    helix: '20.00 mm',
    flute: '4F',
    offsetStatus: 'measured',
  },
  {
    no: 'T02',
    type: 'Ball Nose',
    diameter: '8.00 mm',
    toolLength: '62.00 mm',
    helix: '25.00 mm',
    flute: '2F',
    offsetStatus: 'measured',
  },
  {
    no: 'T03',
    type: 'R-End Mill',
    diameter: '10.00 mm',
    toolLength: '70.00 mm',
    helix: '30.00 mm',
    flute: '3F',
    offsetStatus: 'unMeasured',
  },
  {
    no: 'T04',
    type: 'Engraving',
    diameter: '3.00 mm',
    toolLength: '45.00 mm',
    helix: '12.00 mm',
    flute: '1F',
    offsetStatus: 'measured',
  },
  {
    no: 'T05',
    type: 'Face Mill',
    diameter: '40.00 mm',
    toolLength: '50.00 mm',
    helix: '8.00 mm',
    flute: '4F',
    offsetStatus: 'unMeasured',
  },
  {
    no: 'T06',
    type: 'Probe',
    diameter: '4.00 mm',
    toolLength: '80.00 mm',
    helix: '0.00 mm',
    flute: '1F',
    offsetStatus: 'measured',
  },
  {
    no: 'T07',
    type: 'Drill',
    diameter: '5.00 mm',
    toolLength: '66.00 mm',
    helix: '35.00 mm',
    flute: '2F',
    offsetStatus: 'unMeasured',
  },
];

/** Reads the tool table from the backend and overlays it on the fixed T01..T10
 *  skeleton (a missing key is an empty pocket). Throws on network failure so the
 *  caller can decide whether to keep the last-known table. */
export async function fetchTools(): Promise<ToolRow[]> {
  // No backend in the web/demo build — serve the baked-in seed table instead.
  if (DEMO_MODE) {
    return TOOL_NUMBERS.map((no) => DEMO_TOOLS.find((t) => t.no === no) ?? emptySlot(no));
  }
  const res = await fetch(`${API_BASE}/tools`);
  if (!res.ok) throw new Error(`GET /tools ${res.status.toString()}`);
  const data = (await res.json()) as ToolTableJson;
  const table = data.ToolTable ?? {};
  return TOOL_NUMBERS.map((no) => {
    const entry = table[no];
    return entry ? rowFromJson(no, entry) : emptySlot(no);
  });
}

/** Persists the full table. Empty pockets are omitted so the JSON only carries
 *  filled tools (matching what the remap python expects to read). */
export async function persistTools(tools: ToolRow[]): Promise<void> {
  // No backend in the web/demo build — edits live only in the current tab and
  // reset to the seed table on reload, so there is nothing to POST.
  if (DEMO_MODE) return;
  const ToolTable: Record<string, ToolJson> = {};
  for (const tool of tools) {
    if (!isEmpty(tool)) ToolTable[tool.no] = jsonFromRow(tool);
  }
  const res = await fetch(`${API_BASE}/tools`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ToolTable }),
  });
  if (!res.ok) throw new Error(`POST /tools ${res.status.toString()}`);
}
